import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import {
  ArrowLeft,
  Moon,
  Sun,
  Bell,
  BedDouble,
  Sparkles,
  Info,
} from "lucide-react";
import { loadHealth, updateToday } from "@/lib/healthStore";

const KEY = "silentscan_sleep_v1";

const loadSleep = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : { logs: [], bedtime: "22:30", wake: "06:30" };
  } catch {
    return { logs: [], bedtime: "22:30", wake: "06:30" };
  }
};

const saveSleep = (v) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    // ignore
  }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

// Recovery score = weighted duration + regularity (based on last 7 nights).
const recoveryScore = (logs, bedtime) => {
  if (!logs.length) return 0;
  const last7 = logs.slice(-7);
  const durAvg =
    last7.reduce((s, l) => s + (l.hours || 0), 0) / last7.length;
  // Duration factor
  let dur = 0;
  if (durAvg >= 7.5 && durAvg <= 9) dur = 100;
  else if (durAvg >= 7 && durAvg < 7.5) dur = 85;
  else if (durAvg >= 6.5 && durAvg < 7) dur = 70;
  else if (durAvg >= 6 && durAvg < 6.5) dur = 55;
  else if (durAvg >= 5 && durAvg < 6) dur = 40;
  else dur = 25;
  // Regularity: stddev of bedtime hour
  const toMinutes = (t) => {
    if (!t) return null;
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };
  const beds = last7.map((l) => toMinutes(l.bedtime)).filter((n) => n != null);
  let reg = 100;
  if (beds.length >= 2) {
    const mean = beds.reduce((a, b) => a + b, 0) / beds.length;
    const variance = beds.reduce((a, b) => a + (b - mean) ** 2, 0) / beds.length;
    const sd = Math.sqrt(variance); // minutes
    reg = Math.max(30, 100 - sd);
  }
  return Math.round(dur * 0.7 + reg * 0.3);
};

const computeHours = (bedtime, wake) => {
  if (!bedtime || !wake) return 0;
  const [bh, bm] = bedtime.split(":").map(Number);
  const [wh, wm] = wake.split(":").map(Number);
  let start = bh * 60 + bm;
  let end = wh * 60 + wm;
  if (end < start) end += 24 * 60;
  return Math.round(((end - start) / 60) * 10) / 10;
};

export default function Sleep() {
  const [data, setData] = useState(() => loadSleep());
  const [bedtime, setBedtime] = useState(data.bedtime || "22:30");
  const [wake, setWake] = useState(data.wake || "06:30");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const hours = useMemo(() => computeHours(bedtime, wake), [bedtime, wake]);
  const score = useMemo(() => recoveryScore(data.logs || [], bedtime), [data, bedtime]);

  const logNight = () => {
    const t = todayStr();
    const entry = { date: t, bedtime, wake, hours };
    const logs = [...(data.logs || []).filter((l) => l.date !== t), entry].slice(-30);
    const next = { ...data, logs, bedtime, wake };
    setData(next);
    saveSleep(next);
    // also update dashboard's today
    updateToday({ sleep_hours: hours });
  };

  const chartData = useMemo(() => {
    // Build last 7 days from logs (fill blanks with 0)
    const map = {};
    (data.logs || []).forEach((l) => (map[l.date] = l.hours));
    return Array.from({ length: 7 }).map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      const key = d.toISOString().slice(0, 10);
      return {
        day: d.toLocaleDateString("en-US", { weekday: "short" }),
        hours: map[key] || 0,
      };
    });
  }, [data.logs]);

  const scoreColor =
    score >= 80 ? "#34d399" : score >= 60 ? "#22d3ee" : score >= 40 ? "#fbbf24" : "#f87171";
  const scoreLabel =
    score >= 80 ? "Well-rested" : score >= 60 ? "Balanced" : score >= 40 ? "Watch" : "Rest debt";

  return (
    <div className="min-h-screen bg-[#05050A] text-white pb-24">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(167,139,250,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(59,130,246,0.15) 0%, transparent 60%)",
        }}
      />
      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="sleep-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <Link
            to="/dashboard"
            className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
          >
            Dashboard
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-12">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-violet-300/80">
            Chapter 11 · Sleep
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            The rest{" "}
            <span className="italic text-violet-300">that rebuilds you.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            Sleep is where hormones balance, memory settles and the immune
            system recovers. Log your nights and see your recovery trend.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-4">
          {/* Log */}
          <div
            data-testid="sleep-log-card"
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          >
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              Last night
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5 flex items-center gap-1.5">
                  <Moon className="w-3 h-3 text-violet-300" /> Bedtime
                </div>
                <input
                  data-testid="sleep-bedtime"
                  type="time"
                  value={bedtime}
                  onChange={(e) => setBedtime(e.target.value)}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-white focus:outline-none focus:border-cyan-400"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5 flex items-center gap-1.5">
                  <Sun className="w-3 h-3 text-amber-300" /> Wake
                </div>
                <input
                  data-testid="sleep-wake"
                  type="time"
                  value={wake}
                  onChange={(e) => setWake(e.target.value)}
                  className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2.5 text-white focus:outline-none focus:border-cyan-400"
                />
              </label>
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <div className="font-display text-5xl font-semibold text-white tabular-nums">
                {hours}
              </div>
              <div className="text-sm text-zinc-500">hours of sleep</div>
            </div>
            <button
              data-testid="sleep-log-btn"
              onClick={logNight}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-violet-400 text-black font-medium px-4 py-2.5 text-sm hover:bg-violet-300"
            >
              <BedDouble className="w-4 h-4" />
              Log tonight
            </button>
          </div>

          {/* Score */}
          <div
            data-testid="sleep-score-card"
            className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
          >
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
              7-day recovery score
            </div>
            <motion.div
              key={score}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-4 flex items-baseline gap-3"
            >
              <div
                className="font-display text-7xl font-semibold tabular-nums"
                style={{ color: scoreColor }}
              >
                {score}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: scoreColor }}>
                  {scoreLabel}
                </div>
                <div className="text-xs text-zinc-500">/100</div>
              </div>
            </motion.div>

            <div className="mt-6 h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="sleepBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a78bfa" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#a78bfa" stopOpacity={0.2} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis
                    dataKey="day"
                    stroke="rgba(255,255,255,0.35)"
                    tick={{ fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 10]}
                    stroke="rgba(255,255,255,0.35)"
                    tick={{ fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={30}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "#0A0A14",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 12,
                      fontSize: 12,
                      color: "#fff",
                    }}
                  />
                  <ReferenceLine y={7} stroke="rgba(255,255,255,0.2)" strokeDasharray="4 4" />
                  <Bar dataKey="hours" radius={[6, 6, 0, 0]} fill="url(#sleepBar)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-[11px] text-zinc-500 text-center">
              Dashed line = 7 hour reference · aim for consistent bedtime
            </div>
          </div>
        </div>

        {/* Reminders + tips */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
              <Bell className="w-3.5 h-3.5" />
              Reminders
            </div>
            <ul className="mt-3 flex flex-col gap-2 text-sm text-zinc-200">
              <li className="flex items-center justify-between">
                <span>Wind-down at</span>
                <span className="tabular-nums text-cyan-300">
                  {(() => {
                    const [h, m] = (bedtime || "22:30").split(":").map(Number);
                    const start = new Date();
                    start.setHours(h, m - 30);
                    return start.toTimeString().slice(0, 5);
                  })()}
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span>Screens off by</span>
                <span className="tabular-nums text-cyan-300">
                  {(() => {
                    const [h, m] = (bedtime || "22:30").split(":").map(Number);
                    const off = new Date();
                    off.setHours(h - 1, m);
                    return off.toTimeString().slice(0, 5);
                  })()}
                </span>
              </li>
              <li className="flex items-center justify-between">
                <span>Wake at</span>
                <span className="tabular-nums text-cyan-300">{wake}</span>
              </li>
              <li className="text-[11px] text-zinc-500 pt-2 border-t border-white/5">
                (Reminders live on this device only.)
              </li>
            </ul>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              Tonight&apos;s tips
            </div>
            <ul className="mt-3 flex flex-col gap-2 text-sm text-zinc-200">
              <li className="flex items-start gap-2"><span className="text-emerald-400 mt-1">•</span>Keep the bedroom slightly cool — 18–22°C helps deep sleep.</li>
              <li className="flex items-start gap-2"><span className="text-emerald-400 mt-1">•</span>Skip caffeine after 2 pm; it lingers 6+ hours.</li>
              <li className="flex items-start gap-2"><span className="text-emerald-400 mt-1">•</span>Get 15 minutes of morning sunlight to tune your body clock.</li>
              <li className="flex items-start gap-2"><span className="text-emerald-400 mt-1">•</span>Consistent wake-up time matters more than bedtime.</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 text-cyan-300 flex-shrink-0" />
          <p className="text-[11px] text-zinc-400 leading-relaxed">
            Educational insights, not medical advice. If you snore heavily, gasp
            during sleep, or feel exhausted despite sleeping 7+ hours, please
            consult a clinician — you may be a candidate for a sleep study.
          </p>
        </div>
      </div>
    </div>
  );
}
